import { Box, Button, Dialog, DialogContent, DialogTitle, DialogActions, FormControl, FormLabel, ListSubheader, MenuItem, Select, TextField, TextFieldProps, Typography, Chip, FormHelperText, Popover, IconButton } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import frLocale from 'date-fns/locale/fr';
import { format, addDays, startOfWeek, endOfWeek, isSameMonth, isSameDay, isToday } from 'date-fns';
import { CalendarToday } from '@mui/icons-material';
import { useEffect, useState } from "react";
import {
    darkGreenColor,
    greenColor,
    lightGrayColor,
    redColor,
    requestLoading,
    requestPending,
    supportTask,
    tagColors,
    whiteColor
} from '../../../constants/constants';
import { Project } from '../../../core/models/projectModel';
import { Tag } from '../../../core/models/tagModel';
import {
    closeSaveRecordDialog,
    cleanSaveRecordDialog,
    fetchProjectTags,
    fetchProjectTasks,
    fetchRoles,
    saveRecord,
    saveMultipleRecords,
    updateRecordThunk,
    selectProject,
    selectStage,
    selectTags,
    selectTask,
    setDate,
    setDescription,
    setRole,
    setTicketId,
    setTimeQty
} from '../../../core/state/dialogs/saveRecordDialogSlice';
import { useAppDispatch, useAppSelector } from '../../../core/state/hooks';

export default function SaveRecordDialog(props: any) {
    const dispatch = useAppDispatch();
    const [errors, setErrors] = useState({ project: false, stage: false, task: false, hours: false, supportId: false });
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [isMultipleDaysMode, setIsMultipleDaysMode] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const { isOpen: dialogIsOpen, tasks: projectTasks, tags: projectTags, roles, selectedData } = useAppSelector((state) => state.saveRecordDialog);
    const { user, projectsByClient, projectStatus } = useAppSelector((state) => state.user);
    const isLoadingProjects = projectStatus === requestLoading || projectStatus === requestPending;
    const handleSelectProject = (event: any) => {
        if (event.target.value) {
            dispatch(selectProject(event.target.value));
        }
    }

    const handleSelectStage = (event: any) => {
        if (event.target.value) {
            dispatch(selectStage(event.target.value));
            const roleId = getDefaultRole(event.target.value);
            if (roleId) dispatch(setRole(roleId));
        }
    }

    const getTaskNameById = (taskId: number) => {
        const task = projectTasks.find(t => t.id === taskId);
        return task ? task.name : null;
    }

    const getDefaultRole = (stageId: number) => {
        let roleId = 0;
        projectsByClient.forEach((client) =>
            client.projects &&
            client.projects.forEach((project) =>
                project.stages &&
                project.stages.forEach((stage) =>
                    stage.stageResources &&
                    stage.stageResources.forEach((stageResource) => {
                        if (stageResource.resourceId === user.id && stageId === stage.id) {
                            roleId = stage.stageResources[0].projectRoleId;
                        }
                    }
                    )
                )
            )
        )
        return roleId;
    }

    const handleSelectRole = (event: any) => {
        if (event.target.value) {
            dispatch(setRole(event.target.value));
        }
    }

    const handleDateToggle = (date: Date) => {
        const dateString = date.toDateString();
        const isSelected = selectedDates.some(d => d.toDateString() === dateString);
        
        if (isSelected) {
            setSelectedDates(prev => prev.filter(d => d.toDateString() !== dateString));
        } else {
            setSelectedDates(prev => [...prev, date]);
        }
    }

    const handleMultipleDaysToggle = () => {
        // Solo permitir cambiar a múltiples días cuando estamos creando un nuevo registro
        if (!props.isNew) {
            return;
        }
        
        setIsMultipleDaysMode(!isMultipleDaysMode);
        if (!isMultipleDaysMode) {
            // Al activar modo múltiple, agregar la fecha actual
            setSelectedDates([selectedData.date]);
        } else {
            // Al desactivar modo múltiple, limpiar fechas seleccionadas
            setSelectedDates([]);
        }
    }

    const removeDate = (dateToRemove: Date) => {
        setSelectedDates(prev => prev.filter(d => d.toDateString() !== dateToRemove.toDateString()));
    }

    const handleDatePickerClose = () => {
        setIsDatePickerOpen(false);
    }

    const handleDatePickerOpen = () => {
        setIsDatePickerOpen(true);
    }

    const handleCustomCalendarOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCustomCalendarClose = () => {
        setAnchorEl(null);
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            if (direction === 'prev') {
                newMonth.setMonth(prev.getMonth() - 1);
            } else {
                newMonth.setMonth(prev.getMonth() + 1);
            }
            return newMonth;
        });
    };

    const generateCalendarDays = () => {
        // Obtener el primer día del mes actual
        const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        // Obtener el primer día de la semana que contiene el primer día del mes
        const start = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
        const days = [];
        
        // Generar 6 semanas (42 días) para mostrar el mes completo
        for (let i = 0; i < 42; i++) {
            days.push(addDays(start, i));
        }
        
        return days;
    };

    const setStyleSuccess = () => {
        setErrors({ project: false, stage: false, task: false, hours: false, supportId: false });
    }

    const validateRecord = () => {

        let newErrors = { ...errors };
        let isValid = true;

        const validateField = (condition: boolean, fieldName: keyof typeof newErrors) => {
            if (condition) {
                newErrors[fieldName] = true;
                isValid = false;
            } else {
                newErrors[fieldName] = false;
            }
        };

        validateField(!selectedData.ProjectId || selectedData.ProjectId === 0, 'project');
        validateField(!selectedData.StageId || selectedData.StageId === 0, 'stage');
        validateField(!selectedData.TaskId || selectedData.TaskId === 0, 'task');
        validateField(selectedData.timeQty === "00:00" || selectedData.timeQty === "00:00:00" || !selectedData.timeQty, 'hours');
        validateField(selectedData.ticketId === '' && getTaskNameById(selectedData.TaskId) === supportTask, 'supportId')

        // Validar que se hayan seleccionado fechas en modo múltiple
        if (isMultipleDaysMode && selectedDates.length === 0) {
            isValid = false;
        }

        setErrors(newErrors);

        return isValid;
    }

    const getRolesForStage = (stageId: number) => {
        if (!stageId) return [];
        const stage = projectsByClient
            .flatMap(client => client.projects)
            .flatMap(project => project.stages)
            .find(stage => stage.id === stageId);

        if (!stage || !stage.stageResources) return [];

        return stage.stageResources
            .filter(stageResource => stageResource.resourceId === user.id)
            .map(stageResource => ({
                id: stageResource.projectRoleId,
                name: roles.find(role => role.id === stageResource.projectRoleId)?.name ?? ''
            }));
    };

    const handleClose = () => {
        dispatch(closeSaveRecordDialog());
        dispatch(cleanSaveRecordDialog());
        setStyleSuccess();
        setSelectedDates([]);
        setIsMultipleDaysMode(false);
        setIsDatePickerOpen(false);
    };

    const saveNewRecord = async () => {
        if (!validateRecord()) {
            return;
        }
        if (getTaskNameById(selectedData.TaskId) !== supportTask) {
            dispatch(setTicketId(''));
        }
        
        const isExistingRecord = selectedData.id && selectedData.id > 0;
        
        dispatch(closeSaveRecordDialog());
        setSelectedDates([]);
        setIsMultipleDaysMode(false);
        setIsDatePickerOpen(false);

        try {
            if (isExistingRecord) {
                await dispatch(updateRecordThunk(user.id));
            } else {
                if (isMultipleDaysMode && selectedDates.length > 0) {
                    await dispatch(saveMultipleRecords({ ResourceId: user.id, dates: selectedDates }));
                } else {
                    await dispatch(saveRecord(user.id));
                }
            }
        } finally {
            dispatch(cleanSaveRecordDialog());
        }
    }

    useEffect(() => {
        if (!dialogIsOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose();
            } else if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                // Solo ejecutar si no estamos en un campo de texto multilinea
                const target = event.target as HTMLElement;
                const isMultiline = target.tagName === 'TEXTAREA';
                
                if (!isMultiline) {
                    event.preventDefault();
                    saveNewRecord();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [dialogIsOpen]);

    useEffect(() => {
        if (selectedData.ProjectId !== 0) {
            dispatch(fetchProjectTasks(selectedData.ProjectId));
            dispatch(fetchProjectTags(selectedData.ProjectId));
            dispatch(fetchRoles());
            dispatch(selectTags(new Array<{ tag: Tag }>()));
        }
    }, [dispatch, selectedData.ProjectId]);

    // Inicializar fechas seleccionadas cuando se abre el diálogo
    useEffect(() => {
        if (dialogIsOpen) {
            setSelectedDates([selectedData.date]);
            // Solo permitir múltiples días cuando estamos creando un nuevo registro
            setIsMultipleDaysMode(false);
            setIsDatePickerOpen(false);
            setAnchorEl(null);
            setCurrentMonth(selectedData.date);
        }
    }, [dialogIsOpen, selectedData.date, props.isNew]);

    return (
        <Dialog 
            open={dialogIsOpen} 
            fullWidth
            onClose={handleClose}
        >
            <DialogTitle sx={{ borderBottom: 2, borderBottomColor: 'rgba(35, 31, 32, 0.1)', pb: 1 }}>
                {props.isNew ? "Crear Registro" : "Editar Registro"}
            </DialogTitle>
            
            <DialogContent sx={{ 
                display: "flex", 
                flexDirection: "column",
                height: isMultipleDaysMode ? '80vh' : 'auto',
                overflow: 'auto',
                p: 3,
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: greenColor,
                    borderRadius: '3px',
                    '&:hover': {
                        backgroundColor: darkGreenColor,
                    }
                }
            }}>
                {/* Primera fila: Proyecto y botón de modo de fecha */}
                <Box sx={{ display: "flex", flexDirection: "row", mb: 2, gap: 2 }}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Proyecto (*)</FormLabel>
                        <Select
                            id="project-select"
                            label="Proyecto"
                            value={selectedData.ProjectId || ''}
                            onChange={handleSelectProject}
                            disabled={isLoadingProjects}
                            displayEmpty
                            renderValue={(selected) => {
                                if (isLoadingProjects) {
                                    return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>Cargando proyectos...</span>;
                                }
                                if (!selected || selected === 0) {
                                    return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>Seleccionar proyecto</span>;
                                }
                                // Buscar el nombre del proyecto seleccionado
                                for (const client of projectsByClient) {
                                    const project = client.projects?.find(p => p.id === selected);
                                    if (project) return project.name;
                                }
                                return '';
                            }}
                            sx={{ backgroundColor: lightGrayColor, borderRadius: 4, border: 2, borderColor: errors.project ? redColor : lightGrayColor }}
                        >
                            {projectsByClient.map((client) => {
                                const clientProjects = [<ListSubheader key={client.id}> {client.name}</ListSubheader>];
                                client.projects && client.projects.forEach((project: Project) => {
                                    if (project.state === "OPEN") {
                                        clientProjects.push(<MenuItem value={project.id} key={project.id}> {project.name} </MenuItem>)
                                    }
                                })
                                return (clientProjects.length === 1 ? [] : clientProjects);
                            })}
                        </Select>
                    </FormControl>
                    
                    {props.isNew && (
                        <Box sx={{ display: 'flex', alignItems: 'end' }}>
                            <Button
                                variant={"outlined"}
                                size="small"
                                onClick={handleMultipleDaysToggle}
                                sx={{ 
                                    backgroundColor: isMultipleDaysMode ? greenColor : 'transparent',
                                    color: isMultipleDaysMode ? whiteColor : greenColor,
                                    borderColor: greenColor,
                                    '&:hover': {
                                        backgroundColor: isMultipleDaysMode ? darkGreenColor : greenColor,
                                        color: whiteColor
                                    }
                                }}
                            >
                                {isMultipleDaysMode ? 'Múltiples días' : 'Un solo día'}
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Segunda fila: Sección de fechas - ocupa todo el ancho */}
                <Box sx={{ mb: 2 }}>
                    <FormLabel>Fecha</FormLabel>
                    {!isMultipleDaysMode ? (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Box sx={{ backgroundColor: lightGrayColor, borderRadius: 4, mt: 1, width: '100%' }}>
                                <DatePicker
                                    inputFormat="dd/MM/yyyy"
                                    value={selectedData.date}
                                    onChange={(selectedDate: Date | null) => { dispatch(setDate(selectedDate)) }}
                                    renderInput={(params: TextFieldProps) => <TextField {...params} fullWidth />}
                                />
                            </Box>
                        </LocalizationProvider>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCustomCalendarOpen}
                                    sx={{ 
                                        backgroundColor: lightGrayColor, 
                                        borderRadius: 4, 
                                        flex: 1,
                                        justifyContent: 'space-between',
                                        textTransform: 'none',
                                        color: 'text.primary',
                                        borderColor: lightGrayColor,
                                        '&:hover': {
                                            backgroundColor: lightGrayColor,
                                            borderColor: greenColor
                                        }
                                    }}
                                    endIcon={<CalendarToday fontSize="small" />}
                                >
                                    {selectedDates.length > 0 ? `${selectedDates.length} fecha${selectedDates.length > 1 ? 's' : ''} seleccionada${selectedDates.length > 1 ? 's' : ''}` : 'Seleccionar fechas'}
                                </Button>
                            </Box>
                            
                            <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={handleCustomCalendarClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                PaperProps={{
                                    sx: {
                                        boxShadow: 3,
                                        border: 1,
                                        borderColor: 'divider'
                                    }
                                }}
                            >
                                <Box sx={{ p: 2, minWidth: 280 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="h6">
                                            {format(currentMonth, 'MMMM yyyy')}
                                        </Typography>
                                        <Box>
                                            <IconButton size="small" onClick={() => navigateMonth('prev')}>
                                                ←
                                            </IconButton>
                                            <IconButton size="small" onClick={() => navigateMonth('next')}>
                                                →
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                                            <Typography key={day} variant="caption" textAlign="center" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                                {day}
                                            </Typography>
                                        ))}
                                    </Box>
                                    
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                                        {generateCalendarDays().map((day, index) => {
                                            const isSelected = selectedDates.some(d => isSameDay(d, day));
                                            const isCurrentMonth = isSameMonth(day, currentMonth);
                                            const isTodayDate = isToday(day);
                                            
                                            return (
                                                <Button
                                                    key={index}
                                                    size="small"
                                                    onClick={() => handleDateToggle(day)}
                                                    sx={{
                                                        minWidth: 32,
                                                        height: 32,
                                                        p: 0,
                                                        position: 'relative',
                                                        backgroundColor: isSelected ? greenColor : 'transparent',
                                                        color: isSelected ? whiteColor : isCurrentMonth ? 'text.primary' : 'text.disabled',
                                                        fontWeight: isCurrentMonth ? 'bold' : 'normal',
                                                        '&:hover': {
                                                            backgroundColor: isSelected ? darkGreenColor : 'action.hover'
                                                        },
                                                        '&::after': isTodayDate ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            bottom: 2,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 4,
                                                            height: 4,
                                                            borderRadius: '50%',
                                                            backgroundColor: isSelected ? whiteColor : greenColor
                                                        } : {}
                                                    }}
                                                >
                                                    {day.getDate()}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Popover>
                            
                            {selectedDates.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                    {selectedDates.map((date, index) => (
                                        <Chip
                                            key={index}
                                            label={format(date, 'dd/MM/yyyy')}
                                            onDelete={() => removeDate(date)}
                                            sx={{ backgroundColor: greenColor, color: whiteColor }}
                                        />
                                    ))}
                                </Box>
                            )}
                            
                            {isMultipleDaysMode && selectedDates.length === 0 && (
                                <FormHelperText error>
                                    Selecciona al menos una fecha
                                </FormHelperText>
                            )}
                        </Box>
                    )}
                </Box>
                
                {/* Tercera fila: Etapa y Horas */}
                <Box sx={{ display: "flex", flexDirection: "row", mb: 2, gap: 2 }}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Etapa (*)</FormLabel>
                        <Select
                            id="stage-select"
                            label="Etapa"
                            value={selectedData.StageId}
                            onChange={handleSelectStage}
                            sx={{ backgroundColor: lightGrayColor, borderRadius: 4, border: 2, borderColor: errors.stage ? redColor : lightGrayColor }}
                        >
                            {projectsByClient &&
                                projectsByClient.map((client) =>
                                    client.projects &&
                                    client.projects.map((project) =>
                                        project.stages &&
                                        project.stages
                                            .filter((stage) => stage.projectId === selectedData.ProjectId)
                                            .sort((a, b) => a.number - b.number) // Sort stages by stage.number
                                            .map((stage) => (
                                                <MenuItem value={stage.id} key={stage.id}>
                                                    {stage.name}
                                                </MenuItem>
                                            ))
                                    )
                                )}
                        </Select>
                    </FormControl>
                    
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={frLocale}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Horas (*)</FormLabel>
                            <Box sx={{ backgroundColor: "#F4F4F4", borderRadius: 4, border: 2, borderColor: errors.hours ? redColor : lightGrayColor }}>
                                <TimePicker
                                    ampm={false}
                                    value={selectedData.timeSelection}
                                    onChange={(event: any) => { dispatch(setTimeQty(event)) }}
                                    renderInput={(params: TextFieldProps) => 
                                    {
                                        return <TextField {...params} />
                                    }}
                                />
                            </Box>
                        </FormControl>
                    </LocalizationProvider>
                </Box>
                
                {/* Cuarta fila: Rol */}
                <Box sx={{ display: "flex", flexDirection: "row", mb: 2 }}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Rol (*)</FormLabel>
                        <Select
                            id="stage-select"
                            label="Rol"
                            value={selectedData.ProjectRoleId}
                            sx={{
                                backgroundColor: lightGrayColor,
                                borderRadius: 4,
                                border: 2,
                                borderColor: errors.stage ? redColor : lightGrayColor
                            }}
                            onChange={handleSelectRole}
                        >
                            {getRolesForStage(selectedData.StageId).map(role => (
                                <MenuItem value={role.id} key={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                
                {/* Quinta fila: Tarea y Tags */}
                <Box sx={{ display: "flex", flexDirection: "row", mb: 2, gap: 2 }}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Tarea (*)</FormLabel>
                        <Select
                            id="task-select"
                            label="Tarea"
                            value={selectedData.TaskId}
                            onChange={(event) => { dispatch(selectTask(event.target.value)) }}
                            sx={{ backgroundColor: lightGrayColor, borderRadius: 5, border: 2, borderColor: errors.task ? redColor : lightGrayColor }}
                        >
                            {projectTasks && projectTasks.map((task) =>
                                <MenuItem value={task.id} key={task.id}>{task.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Tags</FormLabel>
                        <Select
                            id="tags-select"
                            label="Tags"
                            multiple={true}
                            value={selectedData.tagRecords.map((tag) => tag.tagId)}
                            onChange={(event) => { dispatch(selectTags(event.target.value)) }}
                            sx={{ backgroundColor: lightGrayColor, borderRadius: 5 }}
                            renderValue={(selected) =>
                                <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", ml: -1.5 }}>
                                    {selected.map((value: number, _index: React.Key | null | undefined) => (
                                        <Typography variant="subtitle1" key={_index}
                                            sx={{ backgroundColor: tagColors[value % tagColors.length], borderRadius: 5, color: whiteColor, mr: 1, px: "3%", height: "100%" }}
                                        >{projectTags?.filter((tag) => tag.id === value)[0]?.name}</Typography>
                                    ))}
                                </Box>
                            }>
                            {projectTags && projectTags.map((tag) =>
                                <MenuItem value={tag.id} key={tag.id}>{tag.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                
                {/* Sexta fila: ID Ticket de Soporte (condicional) */}
                {getTaskNameById(selectedData.TaskId) === supportTask && (
                    <Box sx={{ display: "flex", flexDirection: "row", mb: 2 }}>
                        <FormControl fullWidth>
                            <FormLabel sx={{ mb: 1 }}>ID Ticket de Soporte (*)</FormLabel>
                            <TextField sx={{ backgroundColor: lightGrayColor, borderRadius: 5, border: 2, borderColor: errors.supportId ? redColor : lightGrayColor }} id="ID Ticket de Soporte"
                                value={selectedData.ticketId}
                                onChange={(event) => { dispatch(setTicketId(event.target.value)); }} />
                        </FormControl>
                    </Box>
                )}
                
                {/* Séptima fila: Descripción */}
                <Box sx={{ display: "flex", flexDirection: "row", mb: 2 }}>
                    <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Descripción</FormLabel>
                        <TextField multiline rows={2} sx={{ backgroundColor: lightGrayColor, borderRadius: 5 }} id="description"
                            value={selectedData.description}
                            onChange={(event) => { dispatch(setDescription(event.target.value)); }} />
                    </FormControl>
                </Box>
            </DialogContent>
            
            {/* Botones fijos en la parte inferior */}
            <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                        sx={{
                            backgroundColor: redColor,
                            color: whiteColor,
                            width: 120,
                            alignSelf: 'start',
                            '&:hover': { backgroundColor: darkGreenColor },
                        }}
                        onClick={handleClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        sx={{
                            backgroundColor: greenColor,
                            color: whiteColor,
                            width: 150,
                            alignSelf: 'end',
                            '&:hover': { backgroundColor: darkGreenColor },
                        }}
                        onClick={saveNewRecord}
                    >
                        {isMultipleDaysMode && selectedDates.length > 1 
                            ? `Guardar (${selectedDates.length})` 
                            : 'Guardar'
                        }
                    </Button>
            </DialogActions>
        </Dialog>
    )
}